/**
 * «Юниверситет» (28.09): одно задание — картинка/ролик, текст и ответ по виду (itemKind):
 *  - один ответ и верно/неверно — тап сразу раскрывает ответ;
 *  - несколько ответов — отметить и «Проверить» (пустой выбор проверить нельзя — баг макета);
 *  - ввод — как norm макета (без регистра, пробелов, точек, запятых, дефисов; ё = е);
 *  - «по порядку» — ItemSteps.
 * Варианты перемешиваются один раз на показ задания, а не на каждый рендер.
 */
import { useState } from "react";
import { inputOk, itemKind, type UniItem, type UniKind } from "@ub/shared/uni";
import { shuffle } from "./ctx";
import { ItemSteps } from "./ItemSteps";
import { UniImg, UniVideo } from "./Media";
import { Rich } from "./rich";
import css from "./run.module.css";
import ui from "./uni.module.css";

export type OnAnswered = (ok: boolean, extra: string) => void;

export function ItemView({ item, onDone }: { item: UniItem; onDone: OnAnswered }) {
  const kind = itemKind(item);
  return (
    <>
      {item.img ? <UniImg rel={item.img} className={css.qimg} /> : null}
      {item.video ? <UniVideo rel={item.video} className={css.qimg} /> : null}
      <p className={css.qt} data-fb-part="вопрос"><Rich text={item.title || item.q || "Расставь по порядку"} /></p>
      {kind === "steps" ? <ItemSteps steps={item.steps ?? []} onDone={onDone} />
        : kind === "input" ? <ItemInput item={item} onDone={onDone} />
          : <ItemChoice item={item} kind={kind} onDone={onDone} />}
    </>
  );
}

function ItemChoice({ item, kind, onDone }: { item: UniItem; kind: UniKind; onDone: OnAnswered }) {
  const bool = kind === "bool";
  const multi = kind === "multi";
  const opts = bool ? ["Верно", "Неверно"] : item.options ?? [];
  const right = bool ? [item.answer ? 0 : 1] : ([] as unknown[]).concat(item.answer ?? []).map(Number);
  const [order] = useState(() => (bool ? [0, 1] : shuffle(opts.map((_, k) => k))));
  const [sel, setSel] = useState<number[]>([]);
  const [shown, setShown] = useState<number[] | null>(null);

  const reveal = (picked: number[]) => {
    if (shown) return;
    setShown(picked);
    setSel([]);
    onDone(picked.length === right.length && picked.every((k) => right.includes(k)), "");
  };

  return (
    <>
      <div className={css.opts} role="group" aria-label={multi ? "Варианты, можно несколько" : "Варианты ответа"}>
        {order.map((k) => {
          const isRight = !!shown && right.includes(k);
          const isWrong = !!shown && !isRight && shown.includes(k);
          const st = isRight ? css.right : isWrong ? css.wrong : !shown && sel.includes(k) ? css.sel : "";
          return (
            <button
              key={k}
              type="button"
              className={`${css.opt} ${st}`}
              data-fb-part="вариант ответа"
              disabled={!!shown}
              aria-pressed={multi && !shown ? sel.includes(k) : undefined}
              onClick={() => (multi ? setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k])) : reveal([k]))}
            >
              {/* в вариантах треки — просто текст: кнопку в кнопку не вкладываем */}
              <Rich text={opts[k]} plain />
              {isRight ? <span className={css.mark}> ✓</span> : isWrong ? <span className={css.mark}> ✕</span> : null}
            </button>
          );
        })}
      </div>
      {multi && !shown ? (
        <div className={ui.acts}>
          <button type="button" className={ui.btn} disabled={!sel.length} onClick={() => reveal(sel)}>Проверить</button>
        </div>
      ) : null}
    </>
  );
}

function ItemInput({ item, onDone }: { item: UniItem; onDone: OnAnswered }) {
  const [v, setV] = useState("");
  const [done, setDone] = useState(false);
  const variants = ([] as unknown[]).concat(item.answer ?? []).map(String);
  /* цифровая клавиатура — только если все варианты из цифр (у «е9», «шкаф влево» — нет) */
  const numeric = variants.length > 0 && variants.every((a) => /^[\d\s.,-]+$/.test(a));

  const check = () => {
    if (done || !v.trim()) return;
    setDone(true);
    const ok = inputOk(item, v);
    onDone(ok, ok ? "" : `Ответ: ${variants[0] ?? ""}. `);
  };

  return (
    <>
      <input
        className={css.inp}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); check(); } }}
        disabled={done}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        inputMode={numeric ? "numeric" : "text"}
        enterKeyHint="done"
        placeholder={item.placeholder || "Ответ"}
        aria-label="Ответ"
      />
      {!done ? (
        <div className={ui.acts}>
          <button type="button" className={ui.btn} disabled={!v.trim()} onClick={check}>Проверить</button>
        </div>
      ) : null}
    </>
  );
}
